import React, { useEffect, useState } from 'react'
import AppProviders from './context'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from 'react-query'
import { apiGet } from 'api'
import { audience } from 'api/helpers'
import RenderApp from './render-app'
import { isNull } from 'lodash'
import EmptyPage from 'layout/empty-page'
import EmptyAccessManagementPage from 'layout/access-management/empty-page'
import DatumEmptyPage from 'datum/layout/empty-page'
import NoAppAccess from 'layout/no-app-access'
import { appCount } from 'layout/app-access'
import {
  ACCESS_MANAGEMENT_ROUTES,
  DATUM_ROUTES,
  USER_ROUTES,
  pathMatches
} from './app-routes'
import Page from 'layout/page'
import { DefaultError } from 'shared/default-error'

const ACCOUNT_ERROR_MESSAGES = {
  user_not_provisioned:
    'No ThirdLine account exists for your login. Please contact your administrator to be added.',
  sso_email_missing:
    'Your single sign-on provider did not return an email address, so we cannot match you to a ThirdLine account. Please contact your administrator.',
  sso_disabled_for_municipality:
    'Single sign-on is disabled for your organization by ThirdLine. SSO availability is controlled by ThirdLine independent of your Microsoft Entra setup, so please contact ThirdLine support to have it turned on.',
  account_linked_to_password:
    'Your ThirdLine account was set up with a username and password, so single sign-on cannot be used. Please contact ThirdLine support to migrate your account to SSO.',
  account_linked_to_different_sso:
    'Your ThirdLine account is linked to a different single sign-on identity. Please contact ThirdLine support to re-link your account.'
}

const DEFAULT_ACCOUNT_ERROR_MESSAGE =
  'Your account could not be loaded. Please contact your administrator or ThirdLine support.'

const accountErrorMessage = (error) => {
  const reason = error?.response?.data?.reason
  return ACCOUNT_ERROR_MESSAGES[reason] || DEFAULT_ACCOUNT_ERROR_MESSAGE
}

function App() {
  const { getAccessTokenSilently, isLoading, loginWithRedirect, logout } = useAuth0()
  const [token, setToken] = useState(null)

  useEffect(() => {
    ;(async () => {
      try {
        const accessToken = await getAccessTokenSilently({ audience })
        setToken(accessToken)
      } catch (error) {
        loginWithRedirect()
      }
    })()
  }, [isLoading, getAccessTokenSilently, loginWithRedirect])

  const {
    isSuccess,
    isError,
    error,
    isLoading: isUserLoading,
    data: currentUser
  } = useQuery(
    ['current-user'],
    async () => {
      const accessToken = await getAccessTokenSilently({ audience })
      const data = await apiGet({ url: 'users', token: accessToken })
      return data
    },
    {
      enabled: !isNull(token),
      retry: false
    }
  )

  if (isError) {
    return (
      <Page title="">
        <div className="vh-100 d-flex flex-column align-items-center justify-content-center gap-3 px-4 text-center">
          <p className="mb-0">{accountErrorMessage(error)}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => logout({ returnTo: window.location.origin })}
          >
            Log out
          </button>
          <DefaultError />
        </div>
      </Page>
    )
  }

  if (isUserLoading || isNull(token)) {
    // Pick the loading shell based on the URL the user is refreshing, so the
    // chrome matches the app they're heading into. Without this, a refresh
    // briefly flashes the analytics-app sidebar before AppProviders mounts
    // and RenderApp swaps in the right Authenticated* component.
    const currentPath = window.location.pathname
    if (pathMatches(currentPath, DATUM_ROUTES)) {
      return <DatumEmptyPage />
    }
    if (pathMatches(currentPath, ACCESS_MANAGEMENT_ROUTES)) {
      return <EmptyAccessManagementPage />
    }
    if (pathMatches(currentPath, USER_ROUTES)) {
      return <EmptyPage />
    }
    // App-neutral path (e.g. /settings) or `/`: match the loading chrome to
    // the app the user was last in (persisted currentApp) so we don't flash
    // the analytics shell before RenderApp resolves the same way. The legacy
    // sodApp key covers SOD-only users mid-migration. See context/index.jsx.
    const storedApp = localStorage.getItem('currentApp')
    if (storedApp === 'datum') {
      return <DatumEmptyPage />
    }
    if (storedApp === 'sod' || localStorage.getItem('sodApp') === 'true') {
      return <EmptyAccessManagementPage />
    }
    return <EmptyPage />
  }

  if (isSuccess && !isNull(token)) {
    // No app access: land on a standalone page instead of falling through
    // into the analytics app, where every data fetch would 403 in a loop.
    if (appCount(currentUser) === 0) {
      return (
        <NoAppAccess
          onLogout={() => logout({ returnTo: window.location.origin })}
        />
      )
    }

    return (
      <AppProviders currentUser={currentUser} token={token}>
        <RenderApp />
      </AppProviders>
    )
  }
}

export default App
